import type { MouseEvent } from "react";
import { useMemo } from "react";
import URI from "urijs";

import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@braintrust/braintrust-ui-components";
import {
  ChatIcon,
  MessageIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useUser } from "@js/apps/common/hooks/use-user";
import { isPublicBidWithoutUser } from "@js/apps/jobs/apps/bids/helpers";
import { BidCardApplicationQAList } from "@js/apps/jobs/components/application-qa-list";
import { useBidListFilters } from "@js/apps/jobs/components/filtering-actions";
import { InvitedBadge } from "@js/apps/jobs/components/invited-badge";
import {
  JobInterviewGradingSummary,
  JobInterviewPendingGradingSummary,
} from "@js/apps/jobs/components/job-interview-grading-summary";
import { RecommendationBadge } from "@js/apps/jobs/components/recommendation-badge";
import { useCanManageJobsOnBehalfOfClient } from "@js/apps/jobs/hooks/can-manage-job-as-core-member";
import type {
  ExpandBid,
  IsBidExpanded,
} from "@js/apps/jobs/hooks/expanded-bids";
import { useOpenMessengerModal } from "@js/apps/messenger/hooks/use-open-messenger-modal";
import { BidSkillChipWithTooltip } from "@js/apps/skills/components";
import { RouterLink } from "@js/components/link";
import { useNavigate } from "@js/hooks/navigate";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import { BidActionsMenu } from "../../bid-actions-menu";
import { BidCTAButton } from "../../bid-cta-button";
import { ExternalIntegrationStatusChip } from "../../external-integration-status-chip";
import { RecommendedByMatcherChip } from "../../recommended-by-matcher-chip";
import { setIndexToScrollTo } from "../../virtualized-job-bid-list/rendered-item-index";
import { getBidItemStatusMeta } from "../helpers";

import style from "./style.module.scss";

export type QASectionProps = {
  bid: EmployerBidListBid;
  link: string;
};

export const QASection = ({ bid, link }: QASectionProps) => {
  const navigate = useNavigate();

  const handleSectionClick = (ev: MouseEvent<HTMLDivElement>) => {
    ev.stopPropagation();
    navigate(link, {
      state: { noScroll: true, disableRemountIfTheSameLocation: true },
    });
  };

  return (
    <>
      <Box
        onClick={handleSectionClick}
        data-testid="qa"
        sx={{ px: "var(--px)" }}
      >
        <BidCardApplicationQAList bid={bid} link={link} />
      </Box>
      <Divider color="beige" style={{ marginBottom: "12px" }} />
    </>
  );
};

export type SkillsSectionProps = {
  bid: EmployerBidListBid;
  link: string;
};

export const SkillsSection = ({ bid, link }: SkillsSectionProps) => {
  const { filters } = useBidListFilters();
  const navigate = useNavigate();
  const isSkillsFilterActive =
    !!filters.skills?.length || !!filters.top_skills?.length;

  const handleSectionClick = (ev: MouseEvent<HTMLDivElement>) => {
    ev.stopPropagation();
    navigate(link, {
      state: { noScroll: true, disableRemountIfTheSameLocation: true },
    });
  };

  if (!bid.freelancer.skills.length) {
    return;
  }

  return (
    <Box
      onClick={handleSectionClick}
      data-testid="skills"
      sx={{ px: "var(--px)" }}
    >
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        {bid.freelancer.skills.map((item) => (
          <BidSkillChipWithTooltip
            key={item.id}
            skill={item.skill}
            isMatchedToFilter={isSkillsFilterActive}
          />
        ))}
      </Stack>
    </Box>
  );
};

type ShowCommentsBtnProps = {
  bidId: number;
  isBidExpanded: IsBidExpanded | null;
  expandBid: ExpandBid | null;
  commentsCount: number;
};

export const ShowCommentsBtn = ({
  bidId,
  isBidExpanded,
  expandBid,
  commentsCount,
}: ShowCommentsBtnProps) => {
  return (
    <IconButton
      variant={
        isBidExpanded && isBidExpanded(bidId)
          ? "white-border-grey-2"
          : "white-border-beige"
      }
      shape="squared"
      size="medium"
      aria-label="Application comments"
      sx={{
        height: "42px",
      }}
      onClick={() => (expandBid ? expandBid(bidId) : null)}
    >
      {!!commentsCount && (
        <Typography component="span" sx={{ marginRight: "5px" }}>
          {commentsCount}
        </Typography>
      )}
      <ChatIcon style={{ fontSize: "19px" }} />
    </IconButton>
  );
};

type CTASectionProps = {
  bid: EmployerBidListBid;
  job: Job;
  link: string;
  index?: number;
};

export const CTASection = ({ bid, job, link, index }: CTASectionProps) => {
  const { openMessengerModal } = useOpenMessengerModal();
  const user = useUser();
  const canManageOnBehalfOfAClient = useCanManageJobsOnBehalfOfClient();
  const hasProfile = !isPublicBidWithoutUser(bid);
  const showBidCTAButton =
    hasProfile && (job.user_is_owner || canManageOnBehalfOfAClient);
  return (
    <Stack
      direction="row"
      sx={{
        flexWrap: { xs: "wrap", lg: "nowrap" },
        gap: 1,
        alignItems: "center",
        ml: "auto",
      }}
    >
      {bid.interview?.scorecard_completed_at && (
        <Button
          shape="squared"
          variant="white-border-beige"
          to={new URI(link).setQuery("tab", "interview").toString()}
          size={"x-small"}
          state={{ noScroll: true }}
          sx={{
            height: "42px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          RouterLink={RouterLink}
        >
          View scorecard
        </Button>
      )}
      {showBidCTAButton && (
        <Box
          sx={{
            display: "grid",
            whiteSpace: "nowrap",
          }}
        >
          <BidCTAButton bid={bid} job={job} index={index} isBidCard />
        </Box>
      )}
      {(!bid.interview || bid.status !== ENUMS.BidStatus.INTERVIEWING) && (
        <Button
          shape="squared"
          variant="white-border-beige"
          to={link}
          size={"x-small"}
          state={{ noScroll: true }}
          sx={{
            height: "42px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          RouterLink={RouterLink}
          onClick={() => {
            if (!index) {
              return;
            }

            setIndexToScrollTo(index);
          }}
        >
          View application
        </Button>
      )}
      {bid.freelancer.user.can_be_messaged && (
        <IconButton
          variant="white-border-beige"
          shape="squared"
          size="medium"
          aria-label="Message talent"
          sx={{
            height: "42px",
            width: "42px",
          }}
          onClick={() =>
            user &&
            openMessengerModal({
              context: {
                participants: [user?.id, bid.freelancer.user.id],
              },
            })
          }
        >
          <MessageIcon style={{ fontSize: "16px" }} />
        </IconButton>
      )}
      <BidActionsMenu
        bid={bid}
        job={job}
        sx={{
          width: "42px",
          justifySelf: "flex-end",
        }}
        index={index}
        includeMakeOfferAction
      />
    </Stack>
  );
};

export const NonSupportedLocationBadge = ({
  bid,
}: {
  bid: EmployerBidListBid;
}) => {
  if (!bid.non_supported_location) {
    return null;
  }

  return (
    <Tooltip title="This talent bank is in a non-supported country. We cannot pay them, so we cannot hire them.">
      <span>
        <Chip
          size="x-small"
          color="red"
          label="Cannot hire, payment restrictions"
          LabelProps={{ sx: { fontSize: "12px !important" } }}
        />
      </span>
    </Tooltip>
  );
};

export const BidCardLabels = ({
  bid,
  job,
}: {
  bid: EmployerBidListBid;
  job: Job;
}) => {
  const isMobile = useMediaQuery("sm");
  const direction = !isMobile ? "row" : "column";
  const columnGap = isMobile ? 0 : 1;

  const showRecommendedByMatcher = !!bid.recommended_by_matcher;
  const showExternalStatusBadge =
    !!bid.external_status && !!job.integration_label;
  const showRecomendationBadge =
    (bid.status === ENUMS.BidStatus.IN_REVIEW_AFTER_AI_INTERVIEW ||
      bid.status === ENUMS.BidStatus.INTERVIEWING) &&
    !!bid.interview &&
    !!bid.interview.is_completed &&
    bid.interview.status === ENUMS.JobInterviewStatus.ROUND_1 &&
    !!bid.interview.rating &&
    !!parseFloat(bid.interview.overall_score || "0");
  const showInvitedBadge = !!bid.is_invited;
  const showNonSupportedLocationBadge = !!bid.non_supported_location;

  const showLabels =
    showRecommendedByMatcher ||
    showInvitedBadge ||
    showRecomendationBadge ||
    showExternalStatusBadge ||
    showNonSupportedLocationBadge;
  if (!showLabels) {
    return <></>;
  }

  return (
    <Stack
      direction={direction}
      sx={{
        flexWrap: "wrap",
        columnGap: columnGap,
        rowGap: 1,
        mt: "2px",
      }}
    >
      {showRecommendedByMatcher && <RecommendedByMatcherChip />}
      {showExternalStatusBadge && (
        <ExternalIntegrationStatusChip bid={bid} job={job} />
      )}
      {showInvitedBadge && <InvitedBadge bid={bid} />}
      {showRecomendationBadge && <RecommendationBadge bid={bid} />}
      {showNonSupportedLocationBadge && <NonSupportedLocationBadge bid={bid} />}
    </Stack>
  );
};

type BidItemStatusLabelProps = {
  bid: EmployerBidListBid;
};

export const BidItemStatusLabel = ({ bid }: BidItemStatusLabelProps) => {
  const { label, color } = getBidItemStatusMeta(
    bid.status,
    bid.interview?.status,
  );

  if (!label) return null;

  return (
    <Typography component="h4" variant="paragraph" sx={{ color }}>
      {label}
    </Typography>
  );
};

export const BidItemGradingSummary = ({
  bid,
  link,
}: {
  bid: EmployerBidListBid;
  link?: string;
}) => {
  const props = useGenerateLinkAction({ link });
  const overallScore = bid?.interview?.overall_score;
  const grade = useMemo(
    () => parseFloat(overallScore?.toString() || ""),
    [overallScore],
  );

  if (!bid.interview) {
    return null;
  }

  if (!bid.interview.is_completed) {
    return <JobInterviewPendingGradingSummary bid={bid} />;
  }

  return (
    <>
      <Box {...props} sx={{ px: "var(--px)" }}>
        <JobInterviewGradingSummary
          titleIcon={`${SETTINGS.STATIC_URL}jobs/ai-job-icon-star.svg`}
          grade={grade}
          title={"AI Interview summary"}
          description={
            !bid.interview.overall_feedback || !grade
              ? "Summary not available"
              : bid.interview.overall_feedback
          }
          isCollapsed
        />
      </Box>
      <Divider color="beige" sx={{ my: 1.5 }} />
    </>
  );
};

export const useGenerateLinkAction = ({ link }: { link?: string }) => {
  const navigate = useNavigate();
  if (!link) {
    return {};
  }

  return {
    className: style.clickableContainer,
    onClick: () => {
      navigate(link, {
        state: { noScroll: true },
      });
    },
    "data-testid": "generated-clickable-container",
  };
};
